import React, { useMemo, useState, useRef, useEffect } from 'react';
import { formatNumber } from '../services/utils';
import { isRowNonEmpty } from '../hooks/useOfferState';
import { FaEye, FaEyeSlash, FaRegTrashAlt, FaGripVertical } from 'react-icons/fa';

const CURRENCY_OPTIONS = [
  'DKK',
  'EUR',
  'USD',
  'AUD',
  'BRL',
  'GBP',
  'CAD',
  'PHP',
  'HKD',
  'INR',
  'IDR',
  'ISK',
  'ILS',
  'JPY',
  'CNY',
  'MYR',
  'MXN',
  'NZD',
  'NOK',
  'PLN',
  'RON',
  'CHF',
  'XDR',
  'SGD',
  'SEK',
  'ZAR',
  'KRW',
  'THB',
  'CZK',
  'TRY',
  'HUF',
  'AED',
  'BGN',
  'HRK',
  'LTL',
  'MAD',
  'OMR',
  'RUB',
  'SCR'
];

function ItemRow({
  index, item, items, handleItemChange, deleteRow,
  filterProducts, filterVendors,
  products, vendors, selectProduct, selectVendor,
  showProductDropdown, setShowProductDropdown, searchingProduct,
  showVendorDropdown, setShowVendorDropdown, searchingVendor,
  fetchAllProducts, fetchVendorsForProduct, setActiveColumn,
  pendingSupplierQueue, advanceSupplierQueue,
  toggleHiddenRow, isReadOnly,
  dragOverIndex, onDragStart, onDragOver, onDrop, onDragEnd
}) {
  const cellClass = 'border-b border-black/5 px-2 py-2 align-middle text-xs text-black/80';
  const numberCellClass = `${cellClass} text-right`;
  const baseInput = 'w-full rounded-md border border-black/10 bg-white px-2 py-1 text-xs text-black/80 shadow-[0_0_0_1px_rgba(255,89,15,0.06)] focus:border-company-main/60 focus:outline-none focus:ring-2 focus:ring-company-main/15';
  const numberInput = `${baseInput} text-right tabular-nums`;
  const textReadOnlyInput = `${baseInput} !bg-gray-100 !text-black/45 cursor-not-allowed`;
  const numberReadOnlyInput = `${numberInput} !bg-gray-100 !text-black/45 cursor-not-allowed`;
  const isHidden = !!item.hidden;
  const rowReadOnly = isReadOnly;
  const editableInput = rowReadOnly ? textReadOnlyInput : baseInput;
  const editableNumberInput = rowReadOnly ? numberReadOnlyInput : numberInput;
  const [unitsFocused, setUnitsFocused] = useState(false);
  const [unitsEditValue, setUnitsEditValue] = useState('');
  const [unitCostFocused, setUnitCostFocused] = useState(false);
  const [unitCostEditValue, setUnitCostEditValue] = useState('');
  const unitCostFocusValueRef = useRef('');
  const [unitPriceFocused, setUnitPriceFocused] = useState(false);
  const [totalVatFocused, setTotalVatFocused] = useState(false);
  const [markupFocused, setMarkupFocused] = useState(false);
  const [markupEditValue, setMarkupEditValue] = useState('');
  const [totalVatEditValue, setTotalVatEditValue] = useState('');
  const totalVatFocusValueRef = useRef('');
  const [unitPriceEditValue, setUnitPriceEditValue] = useState('');
  const unitPriceFocusValueRef = useRef('');
  const [showCurrencyDropdown, setShowCurrencyDropdown] = useState(false);
  const currencyFilter = (item.costcurr || '').toString().trim().toUpperCase();
  const filteredCurrencies = useMemo(() => {
    if (!currencyFilter) return CURRENCY_OPTIONS;
    return CURRENCY_OPTIONS.filter(code => code.includes(currencyFilter));
  }, [currencyFilter]);

  // Dropdown keyboard highlight state
  const [highlightedProductIndex, setHighlightedProductIndex] = useState(-1);
  const [highlightedVendorIndex, setHighlightedVendorIndex] = useState(-1);
  const [highlightedCurrencyIndex, setHighlightedCurrencyIndex] = useState(-1);
  const productDropdownRef = useRef(null);
  const vendorDropdownRef = useRef(null);
  const vendorInputRef = useRef(null);
  const currencyDropdownRef = useRef(null);

  useEffect(() => { setHighlightedProductIndex(-1); }, [products]);
  useEffect(() => { setHighlightedVendorIndex(-1); }, [vendors]);
  useEffect(() => { setHighlightedCurrencyIndex(-1); }, [filteredCurrencies]);

  useEffect(() => {
    if (highlightedProductIndex >= 0 && productDropdownRef.current)
      productDropdownRef.current.children[highlightedProductIndex]?.scrollIntoView({ block: 'nearest' });
  }, [highlightedProductIndex]);
  useEffect(() => {
    if (highlightedVendorIndex >= 0 && vendorDropdownRef.current)
      vendorDropdownRef.current.children[highlightedVendorIndex]?.scrollIntoView({ block: 'nearest' });
  }, [highlightedVendorIndex]);
  useEffect(() => {
    if (highlightedCurrencyIndex >= 0 && currencyDropdownRef.current)
      currencyDropdownRef.current.children[highlightedCurrencyIndex]?.scrollIntoView({ block: 'nearest' });
  }, [highlightedCurrencyIndex]);

  // Auto-focus supplier input when this row is the current pending supplier selection
  useEffect(() => {
    if (pendingSupplierQueue?.length > 0 && pendingSupplierQueue[0].rowIndex === index) {
      if (vendorInputRef.current) {
        vendorInputRef.current.focus();
        vendorInputRef.current.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }
  }, [pendingSupplierQueue, index]);

  const EDITABLE_COLS = ['product', 'description', 'supplier', 'units', 'unitcost', 'markup', 'unitprice', 'costcur', 'totalvat'];

  const navigateRow = (direction, col) => {
    const el = document.querySelector(`[data-row="${index + direction}"][data-col="${col}"]`);
    if (el) el.focus();
  };

  const navigateCol = (direction, col) => {
    const idx = EDITABLE_COLS.indexOf(col);
    const nextCol = EDITABLE_COLS[idx + direction];
    if (nextCol) {
      const el = document.querySelector(`[data-row="${index}"][data-col="${nextCol}"]`);
      if (el) el.focus();
    }
  };

  return (
    <tr
      className={`${index % 2 === 1 ? 'bg-company-toned' : 'bg-white'}${dragOverIndex === index ? ' drag-over-row' : ''}`}
      onDragOver={(e) => onDragOver(e, index)}
      onDrop={(e) => onDrop(e, index)}
      onDragEnd={onDragEnd}
    >
      {/* Drag Handle */}
      <td className={`${cellClass} drag-handle-cell`}>
        <div
          className="drag-handle"
          draggable={!rowReadOnly}
          onDragStart={(e) => onDragStart(e, index)}
        >
          <FaGripVertical className="h-3 w-3 text-black/25" />
        </div>
      </td>
      <td className={cellClass}>{index + 1}</td>

      {/* Product */}
      <td className={`${cellClass} relative`}>
        <input
          type="text"
          className={editableInput}
          value={item.product}
          data-row={index}
          data-col="product"
          onChange={(e) => {
            handleItemChange(index, 'product', e.target.value);
            handleItemChange(index, 'product_id', null);
            filterProducts(e.target.value);
            setShowProductDropdown(prev => ({ ...prev, [index]: true }));
          }}
          onFocus={() => fetchAllProducts(index, !!item.product_id)}
          onDoubleClick={() => { if (item.product_id) fetchAllProducts(index); }}
          onBlur={() => {
            setTimeout(() => {
              setShowProductDropdown(prev => ({ ...prev, [index]: false }));
              setHighlightedProductIndex(-1);
            }, 150);
          }}
          onKeyDown={(e) => {
            const isOpen = showProductDropdown[index] && !rowReadOnly && products.length > 0;
            // What Tab/Enter commits when the user has not highlighted anything:
            // the top result, including one found via its description — search
            // by description and pick with Tab is intended behaviour.
            const autoSelectTarget = () => {
              if (highlightedProductIndex >= 0) return products[highlightedProductIndex];
              // Nothing typed means no intent to express, and the unfiltered
              // catalogue is not a match for anything. Focusing an empty product
              // cell opens that full list, so tabbing through a blank row used
              // to stamp whatever sorted first (the product named 80DAYS) on it.
              if (!String(item.product || '').trim()) return null;
              const top = products[0];
              if (!top || top.unfiltered_listing) return null;
              return top;
            };
            if (isOpen) {
              if (e.key === 'ArrowDown') {
                e.preventDefault();
                setHighlightedProductIndex(i => Math.min(i + 1, products.length - 1));
              } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setHighlightedProductIndex(i => Math.max(i - 1, 0));
              } else if (e.key === 'Enter') {
                e.preventDefault();
                const target = autoSelectTarget();
                if (target) { selectProduct(target, index); setHighlightedProductIndex(-1); }
              } else if (e.key === 'Tab') {
                // Select top result (or highlighted) and let Tab move focus naturally
                const target = autoSelectTarget();
                if (target) { selectProduct(target, index); }
                setShowProductDropdown(prev => ({ ...prev, [index]: false }));
                setHighlightedProductIndex(-1);
              } else if (e.key === 'Escape') {
                setShowProductDropdown(prev => ({ ...prev, [index]: false }));
                setHighlightedProductIndex(-1);
              }
            } else {
              if (e.key === 'Enter') {
                e.preventDefault();
                fetchAllProducts(index);
              } else if (e.key === 'ArrowUp') { e.preventDefault(); navigateRow(-1, 'product'); }
              else if (e.key === 'ArrowDown') { e.preventDefault(); navigateRow(1, 'product'); }
              else if (e.key === 'ArrowLeft' && e.target.selectionStart === 0 && e.target.selectionEnd === 0) { e.preventDefault(); navigateCol(-1, 'product'); }
              else if (e.key === 'ArrowRight' && e.target.selectionStart === e.target.value.length && e.target.selectionEnd === e.target.value.length) { e.preventDefault(); navigateCol(1, 'product'); }
            }
          }}
          placeholder="Search product from Zoho"
          disabled={rowReadOnly}
        />
        {searchingProduct[index] && <div className="searching-indicator-inline">...</div>}
        {showProductDropdown[index] && !rowReadOnly && (
          <div className="dropdown" ref={productDropdownRef}>
            {products.length > 0 ? products.map((product, pIdx) => (
              <div
                key={product.id}
                className={`dropdown-item${pIdx === highlightedProductIndex ? ' dropdown-item-active' : ''}`}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => selectProduct(product, index)}
              >
                <div className="dropdown-item-name">
                  {product.code || product.name}
                  {/* Found via the description rather than the code, so the code
                      on its own will not look like what was typed. Say why it is
                      in the list. It is selectable like any other entry. */}
                  {product.matched_description_only && (
                    <span className="dropdown-item-weak"> · beskrivelse</span>
                  )}
                </div>
                <div className="dropdown-item-detail">{product.unit_price ? `DKK ${product.unit_price.toFixed(2)}` : ''}</div>
              </div>
            )) : (
              <div className="dropdown-item" style={{ color: '#888' }}>
                {searchingProduct[index] ? 'Loading...' : 'No products found'}
              </div>
            )}
          </div>
        )}
      </td>

      {/* Description */}
      <td className={cellClass}>
        <input
          type="text"
          className={editableInput}
          value={item.description}
          data-row={index}
          data-col="description"
          onChange={(e) => handleItemChange(index, 'description', e.target.value)}
          onFocus={() => setActiveColumn('Description')}
          onBlur={() => setActiveColumn(null)}
          onKeyDown={(e) => {
            if (e.key === 'ArrowUp') { e.preventDefault(); navigateRow(-1, 'description'); }
            else if (e.key === 'ArrowDown') { e.preventDefault(); navigateRow(1, 'description'); }
            else if (e.key === 'ArrowLeft' && e.target.selectionStart === 0 && e.target.selectionEnd === 0) { e.preventDefault(); navigateCol(-1, 'description'); }
            else if (e.key === 'ArrowRight' && e.target.selectionStart === e.target.value.length && e.target.selectionEnd === e.target.value.length) { e.preventDefault(); navigateCol(1, 'description'); }
          }}
          placeholder="Description"
          disabled={rowReadOnly}
        />
      </td>

      {/* Supplier */}
      <td className={`${cellClass} relative`}>
        <input
          ref={vendorInputRef}
          type="text"
          className={editableInput}
          value={item.supplier_name || ''}
          data-row={index}
          data-col="supplier"
          onChange={(e) => {
            handleItemChange(index, 'supplier_name', e.target.value);
            handleItemChange(index, 'supplier_id', null);
            filterVendors(e.target.value);
            setShowVendorDropdown(prev => ({ ...prev, [index]: true }));
          }}
          onFocus={() => fetchVendorsForProduct(index, !!item.supplier_id)}
          onDoubleClick={() => { if (item.supplier_id) fetchVendorsForProduct(index); }}
          onBlur={() => {
            setTimeout(() => {
              setShowVendorDropdown(prev => ({ ...prev, [index]: false }));
              setHighlightedVendorIndex(-1);
              // If this row was pending supplier selection, advance to next
              if (advanceSupplierQueue) advanceSupplierQueue(index);
            }, 150);
          }}
          onKeyDown={(e) => {
            const isOpen = showVendorDropdown[index] && !rowReadOnly && vendors.length > 0;
            if (isOpen) {
              if (e.key === 'ArrowDown') {
                e.preventDefault();
                setHighlightedVendorIndex(i => Math.min(i + 1, vendors.length - 1));
              } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setHighlightedVendorIndex(i => Math.max(i - 1, 0));
              } else if (e.key === 'Enter' && highlightedVendorIndex >= 0) {
                e.preventDefault();
                selectVendor(vendors[highlightedVendorIndex], index);
                setHighlightedVendorIndex(-1);
              } else if (e.key === 'Tab') {
                // Select top result (or highlighted), then advance queue
                const target = highlightedVendorIndex >= 0 ? vendors[highlightedVendorIndex] : vendors[0];
                if (target) { selectVendor(target, index); }
                setShowVendorDropdown(prev => ({ ...prev, [index]: false }));
                setHighlightedVendorIndex(-1);
                // Prevent default tab navigation if more pending rows in queue
                if (pendingSupplierQueue?.length > 1) {
                  e.preventDefault();
                }
              } else if (e.key === 'Escape') {
                setShowVendorDropdown(prev => ({ ...prev, [index]: false }));
                setHighlightedVendorIndex(-1);
              }
            } else {
              if (e.key === 'Enter') {
                e.preventDefault();
                fetchVendorsForProduct(index);
              } else if (e.key === 'ArrowUp') { e.preventDefault(); navigateRow(-1, 'supplier'); }
              else if (e.key === 'ArrowDown') { e.preventDefault(); navigateRow(1, 'supplier'); }
              else if (e.key === 'ArrowLeft' && e.target.selectionStart === 0 && e.target.selectionEnd === 0) { e.preventDefault(); navigateCol(-1, 'supplier'); }
              else if (e.key === 'ArrowRight' && e.target.selectionStart === e.target.value.length && e.target.selectionEnd === e.target.value.length) { e.preventDefault(); navigateCol(1, 'supplier'); }
            }
          }}
          placeholder="Search supplier from Zoho"
          disabled={rowReadOnly}
        />
        {searchingVendor[index] && <div className="searching-indicator-inline">...</div>}
        {showVendorDropdown[index] && !rowReadOnly && vendors.length > 0 && (
          <div className="dropdown" ref={vendorDropdownRef}>
            {vendors.map((vendor, vIdx) => (
              <div
                key={vendor.id}
                className={`dropdown-item${vIdx === highlightedVendorIndex ? ' dropdown-item-active' : ''}`}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => selectVendor(vendor, index)}
              >
                <div className="dropdown-item-name">{vendor.supplier_code || vendor.name}</div>
              </div>
            ))}
          </div>
        )}
      </td>

      {/* Units */}
      <td className={numberCellClass}>
        <input
          type="text"
          className={editableNumberInput}
          value={unitsFocused ? unitsEditValue : (Number.isFinite(Number(item.quantity)) ? String(parseInt(item.quantity, 10)) : '')}
          data-row={index}
          data-col="units"
          onChange={(e) => {
            const val = e.target.value;
            if (val === '' || val === '-' || /^-?\d+$/.test(val)) {
              setUnitsEditValue(val);
              if (val !== '' && val !== '-') {
                handleItemChange(index, 'quantity', parseInt(val, 10));
              }
            }
          }}
          onFocus={() => {
            setActiveColumn('Units');
            setUnitsFocused(true);
            setUnitsEditValue(Number.isFinite(Number(item.quantity)) ? String(parseInt(item.quantity, 10)) : '');
          }}
          onBlur={(e) => {
            setActiveColumn(null);
            setUnitsFocused(false);
            const parsed = parseInt(e.target.value, 10);
            handleItemChange(index, 'quantity', Number.isFinite(parsed) ? parsed : '');
          }}
          onKeyDown={(e) => {
            if (e.key === 'ArrowUp') { e.preventDefault(); navigateRow(-1, 'units'); }
            else if (e.key === 'ArrowDown') { e.preventDefault(); navigateRow(1, 'units'); }
            else if (e.key === 'ArrowLeft' && e.target.selectionStart === 0 && e.target.selectionEnd === 0) { e.preventDefault(); navigateCol(-1, 'units'); }
            else if (e.key === 'ArrowRight' && e.target.selectionStart === e.target.value.length && e.target.selectionEnd === e.target.value.length) { e.preventDefault(); navigateCol(1, 'units'); }
          }}
          autoComplete="off"
          disabled={rowReadOnly}
        />
      </td>

      {/* UnitCost */}
      <td className={numberCellClass}>
        <input
          type="text"
          className={editableNumberInput}
          value={unitCostFocused
            ? unitCostEditValue
            : formatNumber(item.unit_cost, 2, false)}
          title={item.unit_cost_expr && item.unit_cost_expr !== String(Number(item.unit_cost || 0))
            ? item.unit_cost_expr
            : ''}
          data-row={index}
          data-col="unitcost"
          onFocus={() => {
            setActiveColumn('UnitCost');
            setUnitCostFocused(true);
            const expr = item.unit_cost_expr || '';
            const isSimple = !expr || /^-?[\d.]+$/.test(expr.trim());
            const focusVal = isSimple
              ? (formatNumber(item.unit_cost, 2, false) || '')
              : expr;
            setUnitCostEditValue(focusVal);
            unitCostFocusValueRef.current = focusVal;
          }}
          onBlur={() => {
            setActiveColumn(null);
            setUnitCostFocused(false);
            if (unitCostEditValue !== unitCostFocusValueRef.current) {
              handleItemChange(index, 'unit_cost', unitCostEditValue);
            }
          }}
          onChange={e => {
            setUnitCostEditValue(e.target.value);
            handleItemChange(index, 'unit_cost', e.target.value);
          }}
          onKeyDown={(e) => {
            if (e.key === 'ArrowUp') { e.preventDefault(); navigateRow(-1, 'unitcost'); }
            else if (e.key === 'ArrowDown') { e.preventDefault(); navigateRow(1, 'unitcost'); }
            else if (e.key === 'ArrowLeft' && e.target.selectionStart === 0 && e.target.selectionEnd === 0) { e.preventDefault(); navigateCol(-1, 'unitcost'); }
            else if (e.key === 'ArrowRight' && e.target.selectionStart === e.target.value.length && e.target.selectionEnd === e.target.value.length) { e.preventDefault(); navigateCol(1, 'unitcost'); }
          }}
          min="0"
          step="0.01"
          autoComplete="off"
          disabled={rowReadOnly}
        />
      </td>

      {/* TotalCost */}
      <td className={numberCellClass}>
        <input
          type="text"
          className={numberReadOnlyInput}
          value={formatNumber(item.total_cost, 2, false)}
          readOnly
          tabIndex={-1}
        />
      </td>

      {/* Markup(%) */}
      <td className={numberCellClass}>
        <input
          type="text"
          className={editableNumberInput}
          value={
            markupFocused
              ? markupEditValue
              : (item.markup_percent === '' || item.markup_percent === undefined || isNaN(item.markup_percent) || Number(item.markup_percent) === 0
                  ? ''
                  : formatNumber(item.markup_percent, 2, false))
          }
          data-row={index}
          data-col="markup"
          onFocus={() => {
            setActiveColumn('Markup(%)');
            setMarkupFocused(true);
            // Show value with comma (same as display format) when entering the field
            const v = item.markup_percent;
            setMarkupEditValue(
              v === '' || v === undefined || isNaN(v) || Number(v) === 0
                ? ''
                : formatNumber(v, 2, false)
            );
          }}
          onBlur={e => {
            setActiveColumn(null);
            setMarkupFocused(false);
            // Normalize comma to dot on blur so the stored value is a valid number
            const normalized = markupEditValue.replace(',', '.');
            handleItemChange(index, 'markup_percent', normalized);
          }}
          onChange={e => {
            setMarkupEditValue(e.target.value);
            // Also update state live so unit price recalculates while typing
            handleItemChange(index, 'markup_percent', e.target.value);
          }}
          onKeyDown={(e) => {
            if (e.key === 'ArrowUp') { e.preventDefault(); navigateRow(-1, 'markup'); }
            else if (e.key === 'ArrowDown') { e.preventDefault(); navigateRow(1, 'markup'); }
            else if (e.key === 'ArrowLeft' && e.target.selectionStart === 0 && e.target.selectionEnd === 0) { e.preventDefault(); navigateCol(-1, 'markup'); }
            else if (e.key === 'ArrowRight' && e.target.selectionStart === e.target.value.length && e.target.selectionEnd === e.target.value.length) { e.preventDefault(); navigateCol(1, 'markup'); }
          }}
          min="0"
          step="0.01"
          placeholder="Markup %"
          disabled={rowReadOnly}
          autoComplete="off"
        />
      </td>

      {/* UnitPrice */}
      <td className={numberCellClass}>
        <input
          type="text"
          className={editableNumberInput}
          value={unitPriceFocused
            ? unitPriceEditValue
            : formatNumber(item.unit_price, 2, item.unit_price !== '')}
          title={(() => {
            const adjPerUnit = parseFloat(item.adjustment_per_unit) || 0;
            const baseNum = parseFloat(item.unit_price_base) || 0;
            if (adjPerUnit !== 0) {
              const sign = adjPerUnit > 0 ? '+' : '';
              return `${Number(baseNum.toFixed(4))}${sign}${Number(adjPerUnit.toFixed(4))}`;
            } else if (item.unit_price_expr && item.unit_price_expr !== String(Number(item.unit_price || 0))) {
              return item.unit_price_expr;
            }
            return '';
          })()}
          onFocus={() => {
            setActiveColumn('UnitPrice');
            setUnitPriceFocused(true);
            const adjPerUnit = parseFloat(item.adjustment_per_unit) || 0;
            const baseNum = parseFloat(item.unit_price_base) || 0;
            let focusVal;
            if (adjPerUnit !== 0) {
              const sign = adjPerUnit > 0 ? '+' : '';
              focusVal = `${Number(baseNum.toFixed(4))}${sign}${Number(adjPerUnit.toFixed(4))}`;
            } else {
              const expr = item.unit_price_expr || '';
              const isSimple = !expr || /^-?[\d.]+$/.test(expr.trim());
              focusVal = isSimple
                ? (formatNumber(item.unit_price, 2, false) || '')
                : expr;
            }
            setUnitPriceEditValue(focusVal);
            unitPriceFocusValueRef.current = focusVal;
          }}
          onBlur={e => {
            setActiveColumn(null);
            setUnitPriceFocused(false);
            if (unitPriceEditValue !== unitPriceFocusValueRef.current) {
              handleItemChange(index, 'unit_price', unitPriceEditValue);
            }
          }}
          onChange={e => {
            setUnitPriceEditValue(e.target.value);
            handleItemChange(index, 'unit_price', e.target.value);
          }}
          data-row={index}
          data-col="unitprice"
          onKeyDown={(e) => {
            if (e.key === 'ArrowUp') { e.preventDefault(); navigateRow(-1, 'unitprice'); }
            else if (e.key === 'ArrowDown') { e.preventDefault(); navigateRow(1, 'unitprice'); }
            else if (e.key === 'ArrowLeft' && e.target.selectionStart === 0 && e.target.selectionEnd === 0) { e.preventDefault(); navigateCol(-1, 'unitprice'); }
            else if (e.key === 'ArrowRight' && e.target.selectionStart === e.target.value.length && e.target.selectionEnd === e.target.value.length) { e.preventDefault(); navigateCol(1, 'unitprice'); }
          }}
          min="0"
          step="0.01"
          autoComplete="off"
          disabled={rowReadOnly}
        />
      </td>

      {/* TotalPrice */}
      <td className={numberCellClass}>
        <input
          type="text"
          className={numberReadOnlyInput}
          value={formatNumber(item.total_price, 2, false)}
          readOnly
          tabIndex={-1}
        />
      </td>

      {/* LocalCost */}
      <td className={numberCellClass}>
        <input
          type="text"
          className={numberReadOnlyInput}
          value={item.local_cost === '' || item.local_cost === undefined || item.local_cost === null || isNaN(item.local_cost) || Number(item.local_cost) === 0 ? '' : formatNumber(item.local_cost, 2, false)}
          readOnly
          tabIndex={-1}
        />
      </td>

      {/* CostCur */}
      <td className={`${cellClass} relative`}>
        <input
          type="text"
          className={editableInput}
          value={item.costcurr}
          data-row={index}
          data-col="costcur"
          onFocus={() => {
            if (!rowReadOnly) setShowCurrencyDropdown(true);
          }}
          onBlur={() => {
            setTimeout(() => {
              setShowCurrencyDropdown(false);
              setHighlightedCurrencyIndex(-1);
            }, 150);
          }}
          onChange={(e) => {
            handleItemChange(index, 'costcurr', e.target.value.toUpperCase());
            if (!rowReadOnly) setShowCurrencyDropdown(true);
          }}
          onKeyDown={(e) => {
            const isOpen = showCurrencyDropdown && !rowReadOnly && filteredCurrencies.length > 0;
            if (isOpen) {
              if (e.key === 'ArrowDown') {
                e.preventDefault();
                setHighlightedCurrencyIndex(i => Math.min(i + 1, filteredCurrencies.length - 1));
              } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setHighlightedCurrencyIndex(i => Math.max(i - 1, 0));
              } else if (e.key === 'Enter' && highlightedCurrencyIndex >= 0) {
                e.preventDefault();
                handleItemChange(index, 'costcurr', filteredCurrencies[highlightedCurrencyIndex]);
                setShowCurrencyDropdown(false);
                setHighlightedCurrencyIndex(-1);
              } else if (e.key === 'Escape') {
                setShowCurrencyDropdown(false);
                setHighlightedCurrencyIndex(-1);
              }
            } else {
              if (e.key === 'ArrowUp') { e.preventDefault(); navigateRow(-1, 'costcur'); }
              else if (e.key === 'ArrowDown') { e.preventDefault(); navigateRow(1, 'costcur'); }
              else if (e.key === 'ArrowLeft' && e.target.selectionStart === 0 && e.target.selectionEnd === 0) { e.preventDefault(); navigateCol(-1, 'costcur'); }
              else if (e.key === 'ArrowRight' && e.target.selectionStart === e.target.value.length && e.target.selectionEnd === e.target.value.length) { e.preventDefault(); navigateCol(1, 'costcur'); }
            }
          }}
          placeholder="Currency"
          disabled={rowReadOnly}
          autoComplete="off"
        />
        {showCurrencyDropdown && !rowReadOnly && (
          <div className="dropdown" ref={currencyDropdownRef}>
            {filteredCurrencies.length > 0 ? filteredCurrencies.map((code, cIdx) => (
              <div
                key={code}
                className={`dropdown-item${cIdx === highlightedCurrencyIndex ? ' dropdown-item-active' : ''}`}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  handleItemChange(index, 'costcurr', code);
                  setShowCurrencyDropdown(false);
                  setHighlightedCurrencyIndex(-1);
                }}
              >
                <div className="dropdown-item-name">{code}</div>
              </div>
            )) : (
              <div className="dropdown-item" style={{ color: '#888' }}>
                No currencies found
              </div>
            )}
          </div>
        )}
      </td>

      {/* Profit */}
      <td className={numberCellClass}>
        <input
          type="text"
          className={numberReadOnlyInput}
          value={formatNumber(item.profit, 2)}
          readOnly
          tabIndex={-1}
        />
      </td>

      {/* Profit(%) */}
      <td className={numberCellClass}>
        <input
          type="text"
          className={numberReadOnlyInput}
          value={formatNumber(item.profit_percent, 2)}
          readOnly
          tabIndex={-1}
        />
      </td>

      {/* VAT */}
      <td className={`${cellClass} text-center`}>
        <input
          type="checkbox"
          className="h-4 w-4 accent-company-main"
          checked={parseFloat(item.vat) > 0}
          onChange={e => handleItemChange(index, 'vat', e.target.checked ? 0.25 : 0)}
          disabled={rowReadOnly}
        />
      </td>

      {/* VAT Price */}
      <td className={numberCellClass}>
        <input
          type="text"
          className={numberReadOnlyInput}
          value={formatNumber(item.vat_price, 2)}
          readOnly
          tabIndex={-1}
        />
      </td>
      {/* TotalPrice+VAT */}
       <td className={numberCellClass}>
        <input
          type="text"
          className={editableNumberInput}
          value={totalVatFocused
            ? totalVatEditValue
            : formatNumber(item.total_price_vat, 2, item.unit_price !== '')}
          title={(() => {
            // Legacy adjustment expression (old offers)
            const expr = item.total_vat_adjustment_expr || '';
            const base = parseFloat(item.base_total_price_vat);
            const baseStr = !isNaN(base) && base !== 0 ? (Math.round(base * 100) / 100).toString() : '';
            const full = expr ? `${baseStr}${expr}` : '';
            if (full) return full;
            // TotalPrice+VAT expression (e.g. "20*10" typed with VAT on)
            const tpvExpr = item.total_price_vat_expr || '';
            if (tpvExpr && tpvExpr !== String(Number(item.total_price_vat || 0))) return tpvExpr;
            // Fallback: show unit_price expression (e.g. "50*2" when it matches unitPrice)
            const upExpr = item.unit_price_expr || '';
            if (upExpr && upExpr !== String(Number(item.unit_price || 0))) return upExpr;
            return '';
          })()}
          data-row={index}
          data-col="totalvat"
          onFocus={() => {
            setActiveColumn('TotalPrice+VAT');
            setTotalVatFocused(true);
            const expr = item.total_vat_adjustment_expr || '';
            const base = parseFloat(item.base_total_price_vat);
            let focusVal;
            if (expr) {
              const baseStr = !isNaN(base) && base !== 0 ? (Math.round(base * 100) / 100).toString() : '';
              focusVal = `${baseStr}${expr}`;
            } else {
              focusVal = formatNumber(item.total_price_vat, 2, false) || '';
            }
            setTotalVatEditValue(focusVal);
            totalVatFocusValueRef.current = focusVal;
          }}
          onBlur={e => {
            setActiveColumn(null);
            setTotalVatFocused(false);
            if (totalVatEditValue !== totalVatFocusValueRef.current) {
              handleItemChange(index, 'total_price_vat', totalVatEditValue);
            }
          }}
          onChange={e => setTotalVatEditValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'ArrowUp') { e.preventDefault(); navigateRow(-1, 'totalvat'); }
            else if (e.key === 'ArrowDown') { e.preventDefault(); navigateRow(1, 'totalvat'); }
            else if (e.key === 'ArrowLeft' && e.target.selectionStart === 0 && e.target.selectionEnd === 0) { e.preventDefault(); navigateCol(-1, 'totalvat'); }
            else if (e.key === 'ArrowRight' && e.target.selectionStart === e.target.value.length && e.target.selectionEnd === e.target.value.length) { e.preventDefault(); navigateCol(1, 'totalvat'); }
          }}
          disabled={rowReadOnly}
        />
      </td>

      {/* Action (Delete) */}
      <td className={cellClass}>
        <button
          onClick={() => deleteRow(index)}
          className="inline-flex items-center justify-center text-company-main transition hover:text-company-main/80 disabled:text-black/30"
          disabled={
            rowReadOnly ||
            items.length === 1 ||
            (isRowNonEmpty(item) && items.filter(i => isRowNonEmpty(i)).length <= 1)
          }
          aria-label="Delete row"
        >
          <FaRegTrashAlt className="h-4 w-4" />
        </button>
      </td>

      {/* Action (Hidden) */}
      <td className={cellClass}>
        <button
          onClick={() => toggleHiddenRow(index)}
          className={`inline-flex items-center justify-center transition ${isHidden ? 'text-black/60' : 'text-company-main hover:text-company-main/80'} disabled:text-black/30`}
          disabled={isReadOnly || (isHidden && !!item.product_hidden)}
          title={isHidden && !!item.product_hidden ? 'Hidden by product setting in Zoho' : 'Toggle hidden'}
          aria-label="Toggle hidden"
          aria-pressed={isHidden}
        >
          {isHidden ? <FaEyeSlash className="h-4 w-4" /> : <FaEye className="h-4 w-4" />}
        </button>
      </td>
    </tr>
  );
}

export default ItemRow;
